import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import {
  MedcallDashboardCidLink,
  MedcallMedicationCountPopover,
} from './MedcallClientListRowControls';
import type { MedcallParticipantItem } from './MedcallClientListPage';

import './Medcall2DesktopClientTable.css';

export type Medcall2ColumnSearchField = 'firstname' | 'lastname';
export type Medcall2SortDirection = 'asc' | 'desc';

export type Medcall2DesktopClientTableProps = {
  firstnameSearchText: string;
  lastnameSearchText: string;
  loading: boolean;
  onColumnSearchChange: (field: Medcall2ColumnSearchField, value: string) => void;
  onSortChange: (field: string, direction: Medcall2SortDirection) => void;
  onViewAllMedications: (participant: MedcallParticipantItem) => void;
  participants: MedcallParticipantItem[];
  renderRowActions: (participant: MedcallParticipantItem) => React.ReactNode;
  scope: 'all' | 'my';
  sortDirection: Medcall2SortDirection;
  sortField: string;
};

type ColumnSearchProps = {
  field: Medcall2ColumnSearchField;
  label: string;
  onChange: (field: Medcall2ColumnSearchField, value: string) => void;
  value: string;
};

const normalizeValue = (value: unknown) => `${value || ''}`.trim();

const formatDate = (value: string | null | undefined) => {
  const match = normalizeValue(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return normalizeValue(value);
  }

  return `${match[2]}/${match[3]}/${match[1]}`;
};

const formatNurse = (participant: MedcallParticipantItem) => {
  if (participant.program === 'ODRH Scattered/ERC') {
    return 'N/A';
  }

  const nurse = participant.nurse_name;
  if (!nurse) {
    return '';
  }

  const name = [nurse.firstname, nurse.lastname]
    .map(normalizeValue)
    .filter(Boolean)
    .join(' ');
  const title = normalizeValue(nurse.title);
  if (name && title) {
    return `${name}, ${title}`;
  }

  return name || title;
};

const HighlightedText = ({ searchText, value }: { searchText: string; value: unknown }) => {
  const label = normalizeValue(value);
  const normalizedSearch = searchText.trim();
  if (!normalizedSearch) {
    return <>{label}</>;
  }

  const lowerLabel = label.toLowerCase();
  const lowerSearch = normalizedSearch.toLowerCase();
  const pieces: React.ReactNode[] = [];
  let cursor = 0;
  let matchIndex = lowerLabel.indexOf(lowerSearch, cursor);

  while (matchIndex >= 0) {
    if (matchIndex > cursor) {
      pieces.push(label.slice(cursor, matchIndex));
    }
    const matchEnd = matchIndex + normalizedSearch.length;
    pieces.push(<mark key={`${matchIndex}-${matchEnd}`}>{label.slice(matchIndex, matchEnd)}</mark>);
    cursor = matchEnd;
    matchIndex = lowerLabel.indexOf(lowerSearch, cursor);
  }

  if (cursor < label.length) {
    pieces.push(label.slice(cursor));
  }

  return <>{pieces}</>;
};

const ColumnSearch = ({ field, label, onChange, value }: ColumnSearchProps) => {
  const [draft, setDraft] = useState(value);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open) {
      return;
    }

    setDraft(value);
  }, [open, value]);

  const apply = (close: boolean) => {
    onChange(field, draft);
    if (close) {
      setOpen(false);
    }
  };

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <button
          aria-label={`Filter ${label}`}
          className="medcall2-client-table__filter-trigger"
          data-active={Boolean(value.trim())}
          type="button"
        >
          <Search aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="medcall2-client-table__filter-popover">
        <Input
          aria-label={`Search ${field}`}
          autoFocus
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') {
              return;
            }

            apply(true);
          }}
          placeholder={`Search ${field}`}
          value={draft}
        />
        <div className="medcall2-client-table__filter-actions">
          <Button onClick={() => apply(true)} size="sm" type="button">
            <Search aria-hidden="true" />
            Search
          </Button>
          <Button
            onClick={() => {
              setDraft('');
              onChange(field, '');
              setOpen(false);
            }}
            size="sm"
            type="button"
            variant="outline"
          >
            Reset
          </Button>
          <Button onClick={() => apply(false)} size="sm" type="button" variant="link">
            Filter
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const SortableHeader = ({
  children,
  field,
  onSortChange,
  sortDirection,
  sortField,
}: {
  children: React.ReactNode;
  field: string;
  onSortChange: (field: string, direction: Medcall2SortDirection) => void;
  sortDirection: Medcall2SortDirection;
  sortField: string;
}) => {
  const active = sortField === field;
  const nextDirection: Medcall2SortDirection = active && sortDirection === 'desc' ? 'asc' : 'desc';
  const SortIcon = sortDirection === 'asc' ? ArrowUp : ArrowDown;
  return (
    <button
      aria-label={`Sort ${children} ${nextDirection === 'asc' ? 'ascending' : 'descending'}`}
      className="medcall2-client-table__sort"
      data-active={active}
      onClick={() => onSortChange(field, nextDirection)}
      type="button"
    >
      <span>{children}</span>
      {active ? <SortIcon aria-hidden="true" /> : null}
    </button>
  );
};

export const Medcall2DesktopClientTable = ({
  firstnameSearchText,
  lastnameSearchText,
  loading,
  onColumnSearchChange,
  onSortChange,
  onViewAllMedications,
  participants,
  renderRowActions,
  scope,
  sortDirection,
  sortField,
}: Medcall2DesktopClientTableProps) => {
  const searchByField = useMemo(() => ({
    firstname: firstnameSearchText,
    lastname: lastnameSearchText,
  }), [firstnameSearchText, lastnameSearchText]);

  if (loading && participants.length < 1) {
    return <div className="medcall2-client-table__message">Loading clients...</div>;
  }

  return (
    <div className="medcall2-client-table" data-testid="medcall2-desktop-client-table">
      <div className="medcall2-client-table__scroll">
        <table>
          <colgroup>
            <col style={{ width: '10.89%' }} />
            <col style={{ width: '9.93%' }} />
            <col style={{ width: '4.63%' }} />
            <col style={{ width: '5.37%' }} />
            <col style={{ width: '5.66%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '8.24%' }} />
            <col style={{ width: '7.57%' }} />
            <col style={{ width: '9.71%' }} />
            <col style={{ width: '10.15%' }} />
            <col style={{ width: '6.69%' }} />
            <col style={{ width: '6.16%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>
                <div className="medcall2-client-table__header-with-filter">
                  <SortableHeader
                    field="firstname"
                    onSortChange={onSortChange}
                    sortDirection={sortDirection}
                    sortField={sortField}
                  >Firstname</SortableHeader>
                  <ColumnSearch
                    field="firstname"
                    label="Firstname"
                    onChange={onColumnSearchChange}
                    value={firstnameSearchText}
                  />
                </div>
              </th>
              <th>
                <div className="medcall2-client-table__header-with-filter">
                  <SortableHeader
                    field="lastname"
                    onSortChange={onSortChange}
                    sortDirection={sortDirection}
                    sortField={sortField}
                  >Lastname</SortableHeader>
                  <ColumnSearch
                    field="lastname"
                    label="Lastname"
                    onChange={onColumnSearchChange}
                    value={lastnameSearchText}
                  />
                </div>
              </th>
              <th><SortableHeader field="participant_id" onSortChange={onSortChange} sortDirection={sortDirection} sortField={sortField}>ID</SortableHeader></th>
              <th><SortableHeader field="cid" onSortChange={onSortChange} sortDirection={sortDirection} sortField={sortField}>CID</SortableHeader></th>
              <th><SortableHeader field="dob" onSortChange={onSortChange} sortDirection={sortDirection} sortField={sortField}>DOB</SortableHeader></th>
              <th>Program</th>
              <th><SortableHeader field="provider" onSortChange={onSortChange} sortDirection={sortDirection} sortField={sortField}>Provider</SortableHeader></th>
              <th><SortableHeader field="facility" onSortChange={onSortChange} sortDirection={sortDirection} sortField={sortField}>Facility</SortableHeader></th>
              <th>T.Medications</th>
              <th>Staff Assigned</th>
              <th><SortableHeader field="nurse_name" onSortChange={onSortChange} sortDirection={sortDirection} sortField={sortField}>Nurse</SortableHeader></th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {participants.map((participant) => (
              <tr
                key={participant.id}
                className={participant.active ? undefined : 'medcall2-client-table__inactive'}
              >
                <td><HighlightedText searchText={searchByField.firstname} value={participant.firstname} /></td>
                <td><HighlightedText searchText={searchByField.lastname} value={participant.lastname} /></td>
                <td>{participant.participant_id}</td>
                <td>
                  {scope === 'all' ? (
                    <MedcallDashboardCidLink
                      cid={participant.cid}
                      firstname={participant.firstname}
                      lastname={participant.lastname}
                      participantId={participant.id}
                    />
                  ) : normalizeValue(participant.cid)}
                </td>
                <td>{formatDate(participant.dob)}</td>
                <td>{participant.program}</td>
                <td>{participant.provider}</td>
                <td>{participant.facility}</td>
                <td>
                  <MedcallMedicationCountPopover
                    medicationCount={participant.active_medications_count}
                    medicationNames={participant.active_medication_names}
                    onViewAll={() => onViewAllMedications(participant)}
                    participantId={participant.id}
                  />
                </td>
                <td>{participant.staff_assigned}</td>
                <td>{formatNurse(participant)}</td>
                <td>{renderRowActions(participant)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {participants.length < 1 ? (
        <div className="medcall2-client-table__message">No data</div>
      ) : null}
    </div>
  );
};
